import { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { patch, post } from './api';
import { Alert, Button, Card, Field, Input, Select } from './ui';
import { attempt, useLoad } from './useLoad';

interface Store { id: string; code: string; name: string }
interface Table { id: string; name: string; seats: number | null; active: boolean }
interface Area { id: string; store_id: string; name: string; active: boolean; tables: Table[] }
interface Category { id: string; name: string; station: string | null; active: boolean }

/** Restaurante: salas e mesas de cada loja, e a estação (cozinha, bar) que prepara cada categoria. */
export function TablesPage() {
  const stores = useLoad<{ items: Store[] }>('/stores');
  const areas = useLoad<{ items: Area[] }>('/areas');
  const cats = useLoad<{ items: Category[] }>('/categories');
  const [storeId, setStoreId] = useState<string | null>(null);
  const [newArea, setNewArea] = useState('');
  const [newTable, setNewTable] = useState<Record<string, { name: string; seats: string }>>({});
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { if (!storeId && stores.data?.items[0]) setStoreId(stores.data.items[0].id); }, [stores.data, storeId]);
  if (stores.error || areas.error) return <Alert>{stores.error ?? areas.error}</Alert>;
  if (!stores.data || !areas.data || !cats.data || !storeId) return <p className="text-muted">A carregar…</p>;
  const mine = areas.data.items.filter((a) => a.store_id === storeId);
  const run = async (f: () => Promise<unknown>) => { const e = await attempt(f); setErr(e); if (!e) { void areas.reload(); void cats.reload(); } return !e; };

  return (
    <div className="max-w-4xl space-y-4">
      <Alert tone="info">Nas empresas de restauração, as caixas abrem no mapa de mesas. Cada categoria pode ir para uma estação (Cozinha, Bar…): os pedidos saem agrupados por estação no talão e no ecrã da cozinha.</Alert>
      {err && <Alert>{err}</Alert>}
      {stores.data.items.length > 1 && (
        <Field label="Loja"><Select value={storeId} onChange={(e) => setStoreId(e.target.value)}>{stores.data.items.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.code})</option>)}</Select></Field>
      )}
      <Card title="Nova sala">
        <form className="flex flex-wrap items-end gap-2" onSubmit={async (e) => { e.preventDefault(); if (await run(() => post('/areas', { storeId, name: newArea, sortOrder: mine.length }))) setNewArea(''); }}>
          <Field label="Nome"><Input required value={newArea} onChange={(e) => setNewArea(e.target.value)} placeholder="Sala, Esplanada, Bar…" /></Field>
          <Button><Plus size={16} /> Criar sala</Button>
        </form>
      </Card>
      {mine.map((a) => {
        const nt = newTable[a.id] ?? { name: '', seats: '' };
        return (
          <Card key={a.id} title={a.name} actions={<Button variant="ghost" onClick={() => void run(() => patch(`/areas/${a.id}`, { active: !a.active }))}>{a.active ? 'Desactivar' : 'Activar'}</Button>}>
            <div className="flex flex-wrap gap-2">
              {a.tables.map((t) => (
                <button key={t.id} title={t.active ? 'Desactivar' : 'Activar'} onClick={() => void run(() => patch(`/tables/${t.id}`, { active: !t.active }))}
                  className={`grid h-16 w-20 place-items-center rounded-xl border-2 font-bold ${t.active ? 'border-base' : 'border-dashed opacity-40'}`}>
                  {t.name}{t.seats && <span className="text-xs font-normal text-muted">{t.seats} lug.</span>}
                </button>
              ))}
              {!a.tables.length && <p className="text-muted">Ainda sem mesas.</p>}
            </div>
            <form className="mt-3 flex flex-wrap items-end gap-2" onSubmit={async (e) => {
              e.preventDefault();
              if (await run(() => post('/tables', { areaId: a.id, name: nt.name, seats: nt.seats ? Number(nt.seats) : null, sortOrder: a.tables.length }))) setNewTable({ ...newTable, [a.id]: { name: '', seats: '' } });
            }}>
              <Field label="Mesa"><Input aria-label={`Nova mesa em ${a.name}`} required value={nt.name} onChange={(e) => setNewTable({ ...newTable, [a.id]: { ...nt, name: e.target.value } })} className="w-24" placeholder="12" /></Field>
              <Field label="Lugares"><Input inputMode="numeric" value={nt.seats} onChange={(e) => setNewTable({ ...newTable, [a.id]: { ...nt, seats: e.target.value.replace(/\D/g, '') } })} className="w-20" /></Field>
              <Button variant="ghost"><Plus size={16} /> Mesa</Button>
            </form>
          </Card>
        );
      })}
      <Card title="Estações da cozinha">
        <ul className="divide-y divide-[var(--border)]">
          {cats.data.items.filter((c) => c.active).map((c) => (
            <li key={c.id} className="flex flex-wrap items-center gap-3 py-2">
              <span className="flex-1">{c.name}</span>
              <Select aria-label={`Estação de ${c.name}`} value={c.station ?? ''} onChange={(e) => void run(() => patch(`/categories/${c.id}/station`, { station: e.target.value || null }))} className="w-48">
                <option value="">— não vai à cozinha —</option>
                {[...new Set(['Cozinha', 'Bar', ...cats.data!.items.map((x) => x.station).filter((x): x is string => !!x)])].map((s) => <option key={s} value={s}>{s}</option>)}
              </Select>
            </li>
          ))}
        </ul>
      </Card>
      <ModifierGroups categories={cats.data.items.filter((c) => c.active)} />
    </div>
  );
}

interface Group { id: string; name: string; min_choices: number; max_choices: number; options: { name: string; price: string }[]; product_ids: string[] | null; category_ids: string[] | null; active: boolean }
interface Prod { id: string; name: string; active: boolean }

/** Modificadores e menus: "Ponto da carne" (escolher 1), "Extras" (+preço), "Bebida do menu". */
function ModifierGroups({ categories }: { categories: Category[] }) {
  const list = useLoad<{ items: Group[] }>('/modifier-groups');
  const prods = useLoad<{ items: Prod[] }>('/products');
  const empty = { name: '', min: '0', max: '1', options: [{ name: '', price: '0' }], productIds: [] as string[], categoryIds: [] as string[] };
  const [f, setF] = useState<typeof empty | null>(null);
  const [q, setQ] = useState('');
  const [err, setErr] = useState<string | null>(null);
  if (!list.data || !prods.data) return null;
  const pname = new Map(prods.data.items.map((p) => [p.id, p.name]));
  const cname = new Map(categories.map((c) => [c.id, c.name]));
  const toggle = (arr: string[], id: string) => (arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
  const save = async () => {
    if (!f) return;
    const e = await attempt(() => post('/modifier-groups', {
      name: f.name, min: Number(f.min), max: Number(f.max), options: f.options.filter((o) => o.name.trim()), productIds: f.productIds, categoryIds: f.categoryIds,
    }));
    setErr(e);
    if (!e) { setF(null); void list.reload(); }
  };
  return (
    <Card title="Modificadores e menus" actions={!f && <Button onClick={() => setF({ ...empty, options: [{ name: '', price: '0' }] })}><Plus size={16} /> Novo grupo</Button>}>
      <p className="mb-3 text-sm text-muted">Ao juntar o artigo à comanda, a caixa pede a escolha. Mínimo 1 = obrigatório (ex.: o ponto da carne, a bebida de um menu). O preço das opções soma ao do artigo.</p>
      {err && <div className="mb-3"><Alert>{err}</Alert></div>}
      {f && (
        <div className="mb-4 rounded-xl border border-base p-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Nome"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Ponto da carne" /></Field>
            <Field label="Mínimo"><Input inputMode="numeric" value={f.min} onChange={(e) => setF({ ...f, min: e.target.value.replace(/\D/g, '') })} /></Field>
            <Field label="Máximo"><Input inputMode="numeric" value={f.max} onChange={(e) => setF({ ...f, max: e.target.value.replace(/\D/g, '') })} /></Field>
          </div>
          <div className="mt-3 text-sm font-medium">Opções</div>
          {f.options.map((o, i) => (
            <div key={i} className="mt-2 flex items-center gap-2">
              <Input aria-label="Nome da opção" placeholder="Mal passado" value={o.name} onChange={(e) => setF({ ...f, options: f.options.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} />
              <Input aria-label="Preço da opção" inputMode="decimal" className="w-28" value={o.price} onChange={(e) => setF({ ...f, options: f.options.map((x, j) => (j === i ? { ...x, price: e.target.value } : x)) })} />
              <button aria-label="Retirar opção" onClick={() => setF({ ...f, options: f.options.filter((_, j) => j !== i) })} className="grid h-10 w-10 place-items-center text-brand-700"><Trash2 size={16} /></button>
            </div>
          ))}
          <Button variant="ghost" className="mt-2" onClick={() => setF({ ...f, options: [...f.options, { name: '', price: '0' }] })}><Plus size={16} /> Opção</Button>
          <div className="mt-3 text-sm font-medium">Em que categorias</div>
          <div className="mt-1 flex flex-wrap gap-3">{categories.map((c) => <label key={c.id} className="flex h-9 items-center gap-2"><input type="checkbox" checked={f.categoryIds.includes(c.id)} onChange={() => setF({ ...f, categoryIds: toggle(f.categoryIds, c.id) })} /> {c.name}</label>)}</div>
          <div className="mt-3 flex items-center justify-between gap-2"><span className="text-sm font-medium">ou em que artigos ({f.productIds.length})</span><Input aria-label="Procurar artigos" placeholder="Procurar" value={q} onChange={(e) => setQ(e.target.value)} className="w-48" /></div>
          <div className="mt-1 max-h-48 overflow-y-auto rounded-lg border border-base p-2">
            {prods.data.items.filter((p) => p.active && (!q || p.name.toLowerCase().includes(q.toLowerCase()))).map((p) => (
              <label key={p.id} className="flex h-9 items-center gap-2"><input type="checkbox" checked={f.productIds.includes(p.id)} onChange={() => setF({ ...f, productIds: toggle(f.productIds, p.id) })} /> {p.name}</label>
            ))}
          </div>
          <div className="mt-3 flex gap-2"><Button onClick={save}>Criar grupo</Button><Button variant="ghost" onClick={() => { setF(null); setErr(null); }}>Cancelar</Button></div>
        </div>
      )}
      <ul className="divide-y divide-[var(--border)]">
        {list.data.items.map((g) => (
          <li key={g.id} className={`flex flex-wrap items-center gap-3 py-2 ${g.active ? '' : 'opacity-50'}`}>
            <div className="min-w-0 flex-1">
              <div className="font-semibold">{g.name} <span className="font-normal text-muted">({g.min_choices > 0 ? 'obrigatório' : 'opcional'}, até {g.max_choices})</span></div>
              <div className="text-sm text-muted">{g.options.map((o) => (Number(o.price) ? `${o.name} +${o.price}` : o.name)).join(' · ')}</div>
              <div className="text-xs text-muted">{[...(g.category_ids ?? []).map((id) => cname.get(id) ?? '?'), ...(g.product_ids ?? []).map((id) => pname.get(id) ?? '?')].join(', ')}</div>
            </div>
            <Button variant="ghost" onClick={async () => { setErr(await attempt(() => patch(`/modifier-groups/${g.id}`, { active: !g.active }))); void list.reload(); }}>{g.active ? 'Desactivar' : 'Activar'}</Button>
          </li>
        ))}
        {!list.data.items.length && !f && <li className="py-2 text-muted">Ainda sem grupos.</li>}
      </ul>
    </Card>
  );
}
